import { apiGet, apiPost } from "./helpers/api";
import { postEventStream, UnauthorizedError } from "./helpers/sseStream";

const GENERIC_ERROR =
  "Não foi possível falar com o assistente agora. Tente novamente em alguns instantes!";

// O react-markdown reconstrói a AST inteira a cada render, e o serviço manda
// deltas de poucos caracteres ("Per", "feito", " **"). Sem acumular antes de
// despachar, o chat engasgaria no celular.
const FLUSH_MS = 80;

// O histórico é fonte da verdade do serviço (mantido por uid), então carregamos
// direto de lá — cross-device, sem espelho local. Falha vira conversa vazia: a
// página abre e o erro só aparece se o aluno tentar enviar uma mensagem.
export function fetchAgentHistory(dispatch, agentId) {
  return apiGet(`/api/v1/agents/${agentId}/history`)
    .then((response) =>
      dispatch({
        type: "AGENT_HISTORY_LOADED",
        messages: response.body.data,
        agentId,
      })
    )
    .catch(() =>
      dispatch({ type: "AGENT_HISTORY_LOADED", messages: [], agentId })
    );
}

// Vitrine: traz todos os agentes habilitados (liberados e bloqueados). Carregada
// pela própria página no mount, não mais globalmente pelo menu.
export function fetchAgents(dispatch) {
  dispatch({ type: "AGENTS_LIST_REQUESTED" });

  return apiGet("/api/v1/agents")
    .then((response) =>
      dispatch({ type: "AGENTS_LIST_FETCHED", list: response.body.data })
    )
    .catch(() => dispatch({ type: "AGENTS_LIST_FETCHED", list: [] }));
}

export function fetchAgent(dispatch, agentId) {
  dispatch({ type: "AGENT_FETCH_REQUESTED" });

  return apiGet(`/api/v1/agents/${agentId}`)
    .then((response) =>
      dispatch({ type: "AGENT_FETCHED", agent: response.body.data })
    )
    .catch((err) =>
      dispatch({
        type: "AGENT_FETCH_FAILED",
        forbidden: err?.status === 403 || err?.status === 404,
      })
    );
}

export function sendAgentMessage(dispatch, agentId, text) {
  dispatch({
    type: "AGENT_MESSAGE_SENT",
    message: { id: `user-${Date.now()}`, role: "user", text },
  });

  return apiPost(`/api/v1/agents/${agentId}/messages`)
    .send({ message: text })
    .then((response) =>
      dispatch({
        type: "AGENT_REPLY_RECEIVED",
        message: {
          id: `agent-${Date.now()}`,
          role: "agent",
          text: response.body.data.text,
        },
      })
    )
    .catch((err) =>
      dispatch({
        type: "AGENT_FAILED",
        error: err?.response?.body?.errors || GENERIC_ERROR,
      })
    );
}

// Espelho streaming de sendAgentMessage, usado quando o agente está configurado
// como `streaming` no manager. O texto chega em pedaços e vai sendo acrescentado
// à mesma bolha, em vez de aparecer de uma vez no fim.
export function streamAgentMessage(dispatch, agentId, text) {
  const controller = new AbortController();

  dispatch({
    type: "AGENT_MESSAGE_SENT",
    message: { id: `user-${Date.now()}`, role: "user", text },
  });

  let messageId = null;
  let pending = "";
  let timer = null;
  let finished = false;

  const flush = () => {
    timer = null;
    if (!pending || !messageId) return;

    dispatch({ type: "AGENT_STREAM_DELTA", id: messageId, text: pending });
    pending = "";
  };

  const schedule = () => {
    if (timer === null) timer = setTimeout(flush, FLUSH_MS);
  };

  const handleEvent = ({ name, data }) => {
    if (name === "start") {
      messageId = data.id;
      dispatch({ type: "AGENT_STREAM_STARTED", id: messageId });
    } else if (name === "delta") {
      pending += data.text || "";
      schedule();
    } else if (name === "error") {
      finished = true;
      flush();
      // O parcial permanece na tela: o reducer não mexe em `messages` aqui.
      dispatch({
        type: "AGENT_STREAM_FAILED",
        error: data.message || GENERIC_ERROR,
      });
    } else if (name === "done") {
      finished = true;
      flush();
      dispatch({ type: "AGENT_STREAM_FINISHED" });
    }
  };

  const promise = postEventStream(
    `/api/v1/agents/${agentId}/messages/stream`,
    { message: text },
    { signal: controller.signal, onEvent: handleEvent }
  )
    .then(() => {
      // Servidor fechou sem mandar done nem error: a conexão caiu no meio.
      if (finished) return;

      flush();
      dispatch({ type: "AGENT_STREAM_FAILED", error: GENERIC_ERROR });
    })
    .catch((err) => {
      if (err instanceof UnauthorizedError) return;

      if (err?.name === "AbortError") {
        dispatch({ type: "AGENT_STREAM_ABORTED" });
        return;
      }

      flush();
      dispatch({
        type: "AGENT_STREAM_FAILED",
        error: err?.body?.errors || GENERIC_ERROR,
      });
    })
    .finally(() => {
      if (timer !== null) clearTimeout(timer);
    });

  promise.abort = () => controller.abort();

  return promise;
}
